'use client';

import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@/lib/auth/context';
import { useTwaContext } from '@/lib/twa-detection';

interface TabItem {
  href: string;
  label: string;
  icon: ReactNode;
  kind?: 'add-case' | 'bulk-add' | 'plans' | 'calendar' | 'practice';
}

interface ActionItem {
  label: string;
  icon: ReactNode;
  action: () => void;
}

const IconHome = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 10l8-7 8 7v10a1 1 0 01-1 1h-4v-6h-6v6H5a1 1 0 01-1-1z" />
  </svg>
);

const IconPlus = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.9}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
  </svg>
);

const IconPlan = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 7A2.5 2.5 0 016 4.5h12A2.5 2.5 0 0120.5 7v2H3.5V7z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 11h17v6A2.5 2.5 0 0118 19.5H6A2.5 2.5 0 013.5 17v-6z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 15.5h4" />
  </svg>
);

const IconPlay = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <circle cx="12" cy="12" r="8.5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 8.8v6.4L15.2 12 10 8.8z" />
  </svg>
);

const IconDemoCases = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 4.5h7l3 3v12H7a2 2 0 01-2-2v-11a2 2 0 012-2z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 4.5v3h3M9 11h6M9 14h6M9 17h3" />
  </svg>
);

const IconBriefcase = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6V5a2 2 0 012-2h0a2 2 0 012 2v1m-9 0h14a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18" />
  </svg>
);

const IconUpload = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15V4m0 0l-4 4m4-4l4 4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3" />
  </svg>
);

const IconCalendar = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <rect x="3.5" y="5" width="17" height="15" rx="1.5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 3v4M16 3v4M3.5 10h17" />
  </svg>
);

const IconBell = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.4a2.1 2.1 0 01-.6-1.5V11a6 6 0 00-4-5.7V5a2 2 0 10-4 0v.3A6 6 0 006 11v3.1c0 .6-.2 1.1-.6 1.5L4 17h5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17a3 3 0 01-6 0" />
  </svg>
);

const IconCog = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <circle cx="12" cy="12" r="3" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5v.2a2 2 0 01-4 0v-.2a1.7 1.7 0 00-1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 010-4h.2a1.7 1.7 0 001.5-1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3 1.7 1.7 0 001-1.5V3a2 2 0 014 0v.2a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8 1.7 1.7 0 001.5 1h.2a2 2 0 010 4h-.2a1.7 1.7 0 00-1.5 1z" />
  </svg>
);

const IconLogout = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const IconMore = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.9}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 15l6-6 6 6" />
  </svg>
);

const IconDiscover = (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 20l-4.2-4.2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 7.5v6M7.5 10.5h6" />
  </svg>
);

const tabs: TabItem[] = [
  { href: '/dashboard', label: 'Home', icon: IconHome },
  { href: '/demo-cases', label: 'Demo Cases', icon: IconDemoCases },
  { href: '/dashboard/watch-demo-video', label: 'Watch Demo Video', icon: IconPlay },
  { href: '/practice', label: 'Practice', icon: IconBriefcase, kind: 'practice' },
  { href: '/add-case', label: 'Find Case', icon: IconPlus, kind: 'add-case' },
  { href: '/bulk-add-cases', label: 'Bulk Add', icon: IconUpload, kind: 'bulk-add' },
  { href: '/pricing', label: 'Plans', icon: IconPlan, kind: 'plans' },
  { href: '/calendar', label: 'Calendar', icon: IconCalendar, kind: 'calendar' },
  { href: '/discovery', label: 'Discovery', icon: IconDiscover },
  { href: '/dashboard?tab=activity', label: "What's New", icon: IconBell },
  { href: '/settings', label: 'Settings', icon: IconCog },
];

export default function MobileTabBar({
  className = '',
  showAdvancedWorkspaceItems = false,
  showPracticeItem = false,
  showAddCaseItem = true,
  showBulkAddItem = false,
  showPlansItem: showPlansItemProp = false,
}: {
  className?: string;
  showAdvancedWorkspaceItems?: boolean;
  showPracticeItem?: boolean;
  showAddCaseItem?: boolean;
  showBulkAddItem?: boolean;
  showPlansItem?: boolean;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchKey = searchParams.toString();
  const routeKey = `${pathname}?${searchKey}`;
  const { signOut } = useAuth();
  // Google Play billing gate: never surface the Plans nav inside the Android app.
  const isTwa = useTwaContext();
  const showPlansItem = showPlansItemProp && !isTwa;
  const [openRouteKey, setOpenRouteKey] = useState<string | null>(null);
  const isOpen = openRouteKey === routeKey;
  const showLockedFeatureItems = !showAddCaseItem && showPlansItem;

  const isActive = (href: string) => {
    if (href === '/settings' && pathname === '/settings/labels') return false;
    if (href === '/dashboard?tab=activity') return pathname === '/dashboard' && searchParams.get('tab') === 'activity';
    if (href === '/dashboard') return pathname === '/dashboard' && searchParams.get('tab') !== 'activity';
    return pathname === href;
  };

  const availableTabs = useMemo(
    () =>
      tabs.filter((tab) => {
        if (tab.kind === 'practice') return showPracticeItem;
        if (tab.kind === 'calendar') return showAdvancedWorkspaceItems || showLockedFeatureItems;
        if (tab.kind === 'add-case') return showAddCaseItem || showLockedFeatureItems;
        if (tab.kind === 'bulk-add') return showBulkAddItem || showLockedFeatureItems;
        if (tab.kind === 'plans') return showPlansItem;
        return true;
      }),
    [showAddCaseItem, showAdvancedWorkspaceItems, showBulkAddItem, showLockedFeatureItems, showPracticeItem, showPlansItem],
  );

  const primaryTabs = useMemo(() => {
    const home = availableTabs.find((tab) => tab.href === '/dashboard');
    const practice = availableTabs.find((tab) => tab.kind === 'practice');
    const addCase = availableTabs.find((tab) => tab.kind === 'add-case');
    const plans = availableTabs.find((tab) => tab.kind === 'plans');
    return [home, practice, addCase ?? plans].filter(Boolean) as TabItem[];
  }, [availableTabs]);

  const handleLogout = async () => {
    await signOut();
    window.location.href = '/';
  };

  const actionItems: ActionItem[] = [
    {
      label: 'Logout',
      icon: IconLogout,
      action: handleLogout,
    },
  ];
  const isLockedTab = (tab: TabItem) =>
    showLockedFeatureItems && (tab.kind === 'add-case' || tab.kind === 'bulk-add');

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenRouteKey(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <>
      {isOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-black/10 md:hidden"
          onClick={() => setOpenRouteKey(null)}
        />
      )}

      {isOpen && <div
        id="mobile-more-drawer"
        className="fixed inset-x-3 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-40 rounded-t-lg border border-gray-200 bg-white shadow-xl md:hidden"
      >
        <div className="grid grid-cols-2 gap-2 p-3">
          {availableTabs.map((tab) => {
            const active = isActive(tab.href);
            if (isLockedTab(tab)) {
              return (
                <Link
                  key={tab.href}
                  href="/pricing?source=mobile_case_limit"
                  onClick={() => setOpenRouteKey(null)}
                  className="flex min-h-[44px] items-center gap-3 rounded-md bg-amber-50 px-3 text-left text-sm font-medium text-amber-800"
                >
                  {tab.icon}
                  <span className="min-w-0 truncate">{tab.kind === 'bulk-add' ? 'Unlock bulk add' : 'Unlock case tracking'}</span>
                </Link>
              );
            }

            return (
              <Link
                key={tab.href}
                href={tab.href}
                onClick={() => setOpenRouteKey(null)}
                className={`flex min-h-[44px] items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors ${
                  active ? 'bg-primary/10 text-primary' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.icon}
                <span className="min-w-0 truncate">{tab.label}</span>
              </Link>
            );
          })}
          {actionItems.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={item.action}
              className="flex min-h-[44px] items-center gap-3 rounded-md px-3 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-red-50 hover:text-red-600"
            >
              {item.icon}
              <span className="min-w-0 truncate">{item.label}</span>
            </button>
          ))}
        </div>
      </div>}

      <nav
        className={`fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white safe-area-bottom md:hidden ${className}`}
      >
        <div className="grid h-16 items-center px-2" style={{ gridTemplateColumns: `repeat(${primaryTabs.length + 1}, minmax(0, 1fr))` }}>
          {primaryTabs.map((tab) => {
            const active = isActive(tab.href);
            if (isLockedTab(tab)) {
              return (
                <Link
                  key={tab.href}
                  href="/pricing?source=mobile_case_limit"
                  className="mx-1 flex min-h-12 min-w-0 flex-col items-center justify-center gap-0.5 rounded-xl bg-amber-100 px-1 py-2 text-amber-900"
                >
                  {tab.icon}
                  <span className="max-w-full truncate text-[10px] font-semibold leading-tight">Unlock cases</span>
                </Link>
              );
            }

            const isCaseAction = tab.kind === 'add-case' || tab.kind === 'plans';
            return (
              <Link
                key={tab.href}
                href={tab.href}
                className={`flex min-w-0 flex-col items-center justify-center gap-0.5 px-1 py-2 transition-colors ${
                  isCaseAction
                    ? 'mx-1 min-h-12 rounded-xl bg-primary text-white shadow-sm'
                    : `rounded-md ${active ? 'text-primary' : 'text-gray-500'}`
                }`}
              >
                {tab.icon}
                <span className={`max-w-full truncate text-[10px] leading-tight ${isCaseAction ? 'font-semibold' : 'font-medium'}`}>{tab.label}</span>
              </Link>
            );
          })}
          <button
            type="button"
            aria-controls="mobile-more-drawer"
            aria-expanded={isOpen}
            aria-label="More options"
            onClick={() => setOpenRouteKey((current) => (current === routeKey ? null : routeKey))}
            className={`flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-md px-1 py-2 transition-colors ${
              isOpen ? 'text-primary' : 'text-gray-500'
            }`}
          >
            <span className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>{IconMore}</span>
            <span className="max-w-full truncate text-[10px] font-medium leading-tight">More</span>
          </button>
        </div>
      </nav>
    </>
  );
}
